import { useAuth } from "../../context";
import { useMutation, useQueries, useQuery, useQueryClient } from "react-query";
import { ReportsApi } from "../../api";

export const reportKeys = {
  reportData: () => ["reportData"],
  suppliersReportData: () => ["suppliersReportData"],
  aggregatedSuppliersData: () => ["aggregatedSuppliersData"],
  dimensionReport: () => ["dimensionReport"],
  estimatedPurchasedGoodsAndServices: () => ["estimatedPurchasedGoodsAndServices"],
  capitalGoods: () => ["capitalGoods"],
  upstreamTransportationAndDistribution: () => ["upstreamTransportationAndDistribution"],
  wasteGeneratedInOperations: () => ["wasteGeneratedInOperations"],
  downstreamTransportationAndDistribution: () => ["downstreamTransportationAndDistribution"],
  treatmentOfSoldProducts: () => ["treatmentOfSoldProducts"],
  companyTotalEmissions: () => ["companyTotalEmissions"],
  companyDimensionEmissions: () => ["companyDimensionEmissions"],
  scopeThreeGroupCuts: () => ["scopeThreeGroupCuts"],
  scopeThreeModeCuts: () => ["scopeThreeModeCuts"],
  questionnairesNotes: () => ["questionnairesNotes"],
  questionnairesMethodologyAndSources: () => ["questionnairesMethodologyAndSources"],
  lastThreeYearsEmissions: () => ["lastThreeYearsEmissions"],
  lastThreeYearsEmissionsItemsCount: () => ["lastThreeYearsEmissionsItemsCount"],
  supplierEngagementReports: () => ["supplierEngagementReports"],
  numberOfSupplierQuestionnairesByStatus: () => ["numberOfSupplierQuestionnairesByStatus"],
  numberOfFinalisedSupplierQuestionnairesByType: () => ["numberOfFinalisedSupplierQuestionnairesByType"],
  supplierEmissionsReport: () => ["supplierEmissionsReport"],
  supplierEmissionsPerYear: () => ["supplierEmissionsPerYear"],
  suppliersSpends: () => ["suppliersSpends"],
  suppliersTotalSpends: () => ["suppliersTotalSpends"],
  topFiveInactiveSuppliers: () => ["topFiveInactiveSuppliers"],
  suppliersSpendsByLocation: () => ["suppliersSpendsByLocation"],
  reportId: () => ["reportId"],
  emissionsTargets: () => ["emissionsTargets"]
};

export function useGetReport(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.reportData(), params.year, params.quarter],
    () => callApi(ReportsApi.getCalculatedReport, params), config);
}

// Fetch the calculated report for each year in `years` (fixed quarter). Used by
// the Carbon Intensity settings overlay to list and edit turnover per year.
export function useGetMultiYearReports(years, quarter, config) {
  const { callApi } = useAuth();
  return useQueries(
    years.map((year) => ({
      queryKey: [...reportKeys.reportData(), year, quarter],
      queryFn: () => callApi(ReportsApi.getCalculatedReport, { year, quarter }),
      ...config,
    }))
  );
}

export function useExportReport(config) {
  const { callApi } = useAuth();
  return useMutation(({ params }) => callApi(ReportsApi.getExportReport, params), config);
}

export function useUpdateTurnover(config) {
  const { callApi } = useAuth();
  return useMutation(({reportId, turnover}) => callApi(ReportsApi.updateTurnover, { reportId, turnover }), config);
}

export function useGetSuppliersDataReport(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.suppliersReportData(), params.year, params.quarter],
    () => callApi(ReportsApi.getSuppliersDataReport, params), config);
}

export function useGetDimensionsReport(reportId, dimensionItemIds, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.dimensionReport(), reportId, dimensionItemIds],
    () => callApi(ReportsApi.getDimensionsReport, { reportId, dimensionItemIds }), config);
}

export function useGetAggregatedSuppliersDataReport(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.aggregatedSuppliersData(), params.year, params.quarter],
    () => callApi(ReportsApi.getAggregatedSuppliersDataReport, params), config);
}

export function useExportScopeOneAndTwoTotalData(config) {
  const { callApi } = useAuth();
  return useMutation(({ params }) => callApi(ReportsApi.exportScopeOneAndTwoTotalData, params), config);
}

export function useExportScopeOneAndTwoDimensionsData(reportId, dimensionItemIds, config) {
  const { callApi } = useAuth();
  return useMutation(() => callApi(ReportsApi.exportScopeOneAndTwoDimensionsData, { reportId, dimensionItemIds }), config);
}

export function useExportSuppliersAggregatedData(config) {
  const { callApi } = useAuth();
  return useMutation(({ params }) => callApi(ReportsApi.exportSuppliersAggregatedData, params), config);
}

export function useGetEstimatedPurchasedGoodsAndServices(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.estimatedPurchasedGoodsAndServices(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getEstimatedPurchasedGoodsAndServices, { reportId, params }), config);
}

export function useGetCapitalGoods(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.capitalGoods(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getCapitalGoods, { reportId, params }), config);
}

export function useGetUpstreamTransportationAndDistribution(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.upstreamTransportationAndDistribution(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getUpstreamTransportationAndDistribution, { reportId, params }), config);
}

export function useGetWasteGeneratedInOperations(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.wasteGeneratedInOperations(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getWasteGeneratedInOperations, { reportId, params }), config);
}

export function useGetDownstreamTransportationAndDistribution(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.downstreamTransportationAndDistribution(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getDownstreamTransportationAndDistribution, { reportId, params }), config);
}

export function useGetTreatmentOfSoldProducts(reportId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.treatmentOfSoldProducts(), reportId, params.page, params.size],
    () => callApi(ReportsApi.getTreatmentOfSoldProducts, { reportId, params }), config);
}

export function useGetCompanyTotalEmissionsReports(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.companyTotalEmissions(), params.year, params.quarter],
    () => callApi(ReportsApi.getCompanyTotalEmissionsReports, params), config);
}

// Per-quarter company total-emissions (Scope 3 category totals) for one year,
// plus the whole-year "ALL" figure. Used by the footprint quarterly table.
// Keys mirror useGetCompanyTotalEmissionsReports so card data is reused.
export function useGetQuarterlyCompanyTotalEmissions(year, config) {
  const { callApi } = useAuth();
  const quarters = ["Q1", "Q2", "Q3", "Q4", "ALL"];
  return useQueries(
    quarters.map((quarter) => ({
      queryKey: [...reportKeys.companyTotalEmissions(), year, quarter],
      queryFn: () => callApi(ReportsApi.getCompanyTotalEmissionsReports, { year, quarter }),
      ...config,
    }))
  );
}

// One query per year at a fixed quarter ("ALL" = whole year). Used by the
// Emissions Trends chart to source Scope 3 from the persisted report (the
// same /reports/total-emissions figures the cards and drill-downs show).
export function useGetMultiYearCompanyTotalEmissions(years, quarter, config) {
  const { callApi } = useAuth();
  return useQueries(
    years.map((year) => ({
      queryKey: [...reportKeys.companyTotalEmissions(), year, quarter],
      queryFn: () => callApi(ReportsApi.getCompanyTotalEmissionsReports, { year, quarter }),
      ...config,
    }))
  );
}

// One query per (year, quarter) — Q1..Q4 for every year, year-major order.
// Powers the quarterly mode of the Scope 3 detail-page trend charts. Keys
// mirror useGetCompanyTotalEmissionsReports so per-quarter data fetched
// elsewhere is reused from cache.
export function useGetQuarterlyMultiYearCompanyTotalEmissions(years, config) {
  const { callApi } = useAuth();
  const periods = years.flatMap((year) => ["Q1", "Q2", "Q3", "Q4"].map((quarter) => ({ year, quarter })));
  return useQueries(
    periods.map(({ year, quarter }) => ({
      queryKey: [...reportKeys.companyTotalEmissions(), year, quarter],
      queryFn: () => callApi(ReportsApi.getCompanyTotalEmissionsReports, { year, quarter }),
      ...config,
    }))
  );
}

export function useGetCompanyDimensionReports(dimensionId, params, config) {
  const { callApi } = useAuth();
  // Dimension filter params (dimensionOne..Five) must be part of the key or the
  // table serves stale cache when the user changes the Insights filter.
  const dimensionFilterKey = ["dimensionOne", "dimensionTwo", "dimensionThree", "dimensionFour", "dimensionFive"]
    .map((k) => params[k])
    .join("|");
  // ESG-3960: includeUnattributed changes the payload (it appends the "no {level} assigned" footer row),
  // so it must be part of the key or a cached response from the other setting is served.
  return useQuery([...reportKeys.companyDimensionEmissions(), dimensionId, params.year, params.quarter, params.page, params.size, dimensionFilterKey, !!params.includeUnattributed],
    () => callApi(ReportsApi.getCompanyDimensionReports, { dimensionId, params }), config);
}

// Per-dimension reports for each quarter of `year` plus the whole-year "ALL"
// figure (index 0..3 = Q1..Q4, index 4 = ALL). Powers the quarterly view of
// the drill-down by-dimension tables. Query keys mirror
// useGetCompanyDimensionReports so any period already fetched (e.g. the
// annual view's ALL request) is reused from cache. `params` carries the
// dimension filter (dimensionOne..Five) + page/size; quarter is supplied here.
export function useGetQuarterlyCompanyDimensionReports(dimensionId, params, config) {
  const { callApi } = useAuth();
  const quarters = ["Q1", "Q2", "Q3", "Q4", "ALL"];
  const dimensionFilterKey = ["dimensionOne", "dimensionTwo", "dimensionThree", "dimensionFour", "dimensionFive"]
    .map((k) => params[k])
    .join("|");
  return useQueries(
    quarters.map((quarter) => ({
      queryKey: [...reportKeys.companyDimensionEmissions(), dimensionId, params.year, quarter, params.page, params.size, dimensionFilterKey, !!params.includeUnattributed],
      queryFn: () => callApi(ReportsApi.getCompanyDimensionReports, { dimensionId, params: { ...params, quarter } }),
      ...config,
    }))
  );
}

// ESG-3942: dimension-scoped supplier/product spend cut for Cat 1/2. The dimension
// filter (params.dimensionOne..Five) is part of the key so the cut re-fetches when
// the Insights filter changes; groupBy distinguishes supplier vs product-category.
export function useGetScopeThreeGroupCuts(reportId, category, params, config) {
  const { callApi } = useAuth();
  const dimensionFilterKey = ["dimensionOne", "dimensionTwo", "dimensionThree", "dimensionFour", "dimensionFive"]
    .map((k) => params[k])
    .join("|");
  return useQuery(
    [...reportKeys.scopeThreeGroupCuts(), reportId, category, params.groupBy, params.page, params.size, dimensionFilterKey],
    () => callApi(ReportsApi.getScopeThreeGroupCuts, { reportId, category, params }),
    config
  );
}

// ESG-3942: dimension-scoped transport/travel mode cut for Cat 4/6/7/9.
export function useGetScopeThreeModeCuts(reportId, category, params, config) {
  const { callApi } = useAuth();
  const dimensionFilterKey = ["dimensionOne", "dimensionTwo", "dimensionThree", "dimensionFour", "dimensionFive"]
    .map((k) => params[k])
    .join("|");
  return useQuery(
    [...reportKeys.scopeThreeModeCuts(), reportId, category, dimensionFilterKey],
    () => callApi(ReportsApi.getScopeThreeModeCuts, { reportId, category, params }),
    config
  );
}

export function useGetQuestionnairesNotes(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.questionnairesNotes(), params.year, params.quarter],
    () => callApi(ReportsApi.getQuestionnairesNotes, params), config);
}

export function useGetQuestionnairesMethodologyAndSources(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.questionnairesMethodologyAndSources(), params.year, params.quarter],
    () => callApi(ReportsApi.getQuestionnairesMethodologyAndSources, params), config);
}

export function useGetLastThreeYearsEmissions(dimensionId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.lastThreeYearsEmissions(), dimensionId, params.page, params.size],
    () => callApi(ReportsApi.getLastThreeYearsEmissions, { dimensionId, params }), config);
}

export function useGetLastThreeYearsEmissionsItemsCount(dimensionId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.lastThreeYearsEmissionsItemsCount(), dimensionId],
    () => callApi(ReportsApi.getLastThreeYearsEmissionsItemsCount, dimensionId), config);
}

export function useGetSupplierEngagementReports(categoryId, supplierIds, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.supplierEngagementReports(), categoryId, supplierIds],
    () => callApi(ReportsApi.getSupplierEngagementReports, {categoryId, supplierIds}), config);
}

export function useGetNumberOfSupplierQuestionnairesByStatus(categoryId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.numberOfSupplierQuestionnairesByStatus(), categoryId],
    () => callApi(ReportsApi.getNumberOfSupplierQuestionnairesByStatus, categoryId), config);
}

export function useGetNumberOfFinalisedSupplierQuestionnairesByType(categoryId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.numberOfFinalisedSupplierQuestionnairesByType(), categoryId],
    () => callApi(ReportsApi.getNumberOfFinalisedSupplierQuestionnairesByType, categoryId), config);
}

export function useGetSupplierEmissionsReport(supplierId, params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.supplierEmissionsReport(), supplierId, params.year, params.quarter],
    () => callApi(ReportsApi.getSupplierEmissionsReport, { supplierId, params }), config);
}

export function useGetSupplierEmissionsPerYear(supplierId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.supplierEmissionsPerYear(), supplierId],
    () => callApi(ReportsApi.getSupplierEmissionsPerYear, supplierId), config);
}

export function useGetSuppliersSpends(categoryId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.suppliersSpends(), categoryId],
    () => callApi(ReportsApi.getSuppliersSpends, categoryId), config);
}

export function useGetSuppliersTotalSpends(categoryId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.suppliersTotalSpends(), categoryId],
    () => callApi(ReportsApi.getSuppliersTotalSpends, categoryId), config);
}

export function useGetTopFiveInactiveSuppliers(categoryId, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.topFiveInactiveSuppliers(), categoryId],
    () => callApi(ReportsApi.getTopFiveInactiveSuppliers, categoryId), config);
}

export function useGetSuppliersSpendsByLocation({ year, categoryId, supplierId }, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.suppliersSpendsByLocation(), year, categoryId, supplierId],
    () => callApi(ReportsApi.getSuppliersSpendsByLocation, { year, categoryId, supplierId }), config);
}

export function useExportConsumptionsData(config) {
  const { callApi } = useAuth();
  return useMutation(({ dimensionId, queryParams }) =>
    callApi(ReportsApi.exportConsumptionsData, { dimensionId, queryParams }), config);
}

export function useExportAllDataReport(config) {
  const { callApi } = useAuth();
  return useMutation(({ dimensionId, queryParams }) =>
    callApi(ReportsApi.exportAllDataReport, { dimensionId, queryParams }), config);
}

export function useGetReportId(params, config) {
  const { callApi } = useAuth();
  return useQuery([...reportKeys.reportId(), params.year, params.quarter],
    () => callApi(ReportsApi.getReportId, params), config);
}

// ESG-3934: org-wide emissions reduction targets for the Emissions Trends chart.
export function useGetEmissionsTargets(config) {
  const { callApi } = useAuth();
  return useQuery(reportKeys.emissionsTargets(),
    () => callApi(ReportsApi.getEmissionsTargets), config);
}

export function useCreateEmissionsTarget(config) {
  const { callApi } = useAuth();
  const queryClient = useQueryClient();
  return useMutation((target) => callApi(ReportsApi.createEmissionsTarget, target), {
    ...config,
    onSuccess: (...args) => {
      queryClient.invalidateQueries(reportKeys.emissionsTargets());
      config?.onSuccess?.(...args);
    },
  });
}

export function useUpdateEmissionsTarget(config) {
  const { callApi } = useAuth();
  const queryClient = useQueryClient();
  return useMutation((target) => callApi(ReportsApi.updateEmissionsTarget, target), {
    ...config,
    onSuccess: (...args) => {
      queryClient.invalidateQueries(reportKeys.emissionsTargets());
      config?.onSuccess?.(...args);
    },
  });
}

export function useDeleteEmissionsTarget(config) {
  const { callApi } = useAuth();
  const queryClient = useQueryClient();
  return useMutation((id) => callApi(ReportsApi.deleteEmissionsTarget, id), {
    ...config,
    onSuccess: (...args) => {
      queryClient.invalidateQueries(reportKeys.emissionsTargets());
      config?.onSuccess?.(...args);
    },
  });
}